import { Link } from "react-router-dom";
import { PageHeader } from "@/components/data-display/page-header";
import { KVList } from "@/components/data-display/kv-list";
import { Badge } from "@/components/ui/badge";
import { EnvBadge } from "@/components/ui/status";
import { Button, buttonVariants } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/feedback";
import { LoadingState } from "@/components/ui/system-state";
import { IdentifierValue } from "@/components/ui/identifier-value";
import { ProviderMigrationNav } from "@/features/provider-migration/journey-nav";
import { ReadOnlyBanner, UnavailableWithRetry } from "@/features/provider-migration/shared";
import { useProviderMigrationConnection } from "@/hooks/use-provider-migration";

/**
 * Provider migration, step 1: connection status (page spec provider-migration-connect).
 * GET /v1/reconciliation/provider-migration/connections/current. Shows which provider workspace the
 * gateway is connected to. The credential is a deployment secret reference; no value ever reaches the
 * browser and there is no control here to enter or change one.
 */
export default function ProviderMigrationConnectPage() {
  const { data: connection, loading, error, refresh } = useProviderMigrationConnection();
  // The gateway answered without an error and without a connection: an empty state, not a
  // failure (ALL-5739). An error answer is never reinterpreted as "no connection" here, because
  // the backend contract for that is not published; it is shown as an error with a retry.
  const notConnected = !loading && !error && !connection;

  return (
    <div>
      <PageHeader
        title="Provider migration"
        subtitle="Read-only view of the connected source provider workspace."
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={refresh} disabled={loading}>
              Refresh
            </Button>
          </>
        }
      />
      <ProviderMigrationNav active="connect" />
      <ReadOnlyBanner />

      {loading && <LoadingState rows={4} />}

      {!loading && error && (
        <UnavailableWithRetry title="Connection status is not available" error={error} onRetry={refresh} />
      )}

      {notConnected && (
        <EmptyState
          card
          title="No source provider connected"
          description="An administrator connects the provider workspace in deployment settings."
        />
      )}

      {!loading && !error && connection && (
        <div className="max-w-2xl space-y-4">
          <KVList
            rows={[
              { k: "Provider", v: <span className="capitalize">{connection.provider}</span> },
              { k: "Environment", v: <EnvBadge env={connection.environment === "sandbox" ? "Sandbox" : connection.environment} /> },
              { k: "Workspace", v: <IdentifierValue value={connection.workspaceId} /> },
              {
                k: "Connection health",
                v: connection.healthy ? (
                  <Badge variant="success">Reachable</Badge>
                ) : (
                  <Badge variant="warning">Not reachable</Badge>
                ),
              },
              { k: "Credential reference", v: <IdentifierValue value={connection.secretReferenceName} /> },
              { k: "Latest snapshot", v: connection.snapshotId ? <IdentifierValue value={connection.snapshotId} /> : "None sealed yet" },
              { k: "Last checked", v: connection.lastCheckedAt ?? "—" },
            ]}
          />
          <p className="text-xs text-muted">
            The credential is managed as a deployment secret reference. Its value is never shown or accepted in the browser.
          </p>
          {!connection.healthy && (
            <p className="text-sm text-warning">
              The provider inventory could not be read just now. Discovery will not show data until the connection is reachable.
            </p>
          )}
          {/* One interactive element, not two: a Button nested inside a Link
              gives assistive tech and the keyboard two overlapping controls
              with the same name. The link carries the button's own styling. */}
          <Link
            to="/reconciliation/provider-migration/discovery"
            className={buttonVariants({ size: "md" })}
          >
            Continue to discovery
          </Link>
        </div>
      )}
    </div>
  );
}
